import { CurrencyCell } from '@components/DataTable/CurrencyCell';
import { EllipsisText } from '@components/EllipsisText';
import { type SaleSummary } from '@store/skeletor/clients/sales/summary/types';
import { type ColumnDef } from '@tanstack/react-table';
import { safeFormatToPercentage } from '@utils/columns/safeFormatToPercentage';
import { safeCalculatePercentage } from '@utils/number/safeCalculatePercentage';
import { formatRut } from '@utils/rut/formatRut';

const columns: ColumnDef<SaleSummary>[] = [
  {
    id: 'debtorName',
    accessorKey: 'debtorName',
    enableSorting: true,
    cell({ row }) {
      const { debtorName } = row.original;

      return <EllipsisText>{debtorName}</EllipsisText>;
    },
    header: 'Deudor',
  },
  {
    id: 'debtorRut',
    accessorKey: 'debtorRut',
    enableSorting: true,
    cell({ row }) {
      const { debtorDv, debtorRut } = row.original;

      return (
        <p className="max-w-[30ch] text-xs capitalize">
          {formatRut(`${debtorRut}${debtorDv}`)}
        </p>
      );
    },
    header: 'Rut deudor',
  },
  {
    id: 'totalSale',
    accessorKey: 'totalSale',
    cell: CurrencyCell,
    enableSorting: true,
    header: 'Ventas',
    meta: {
      className: 'text-end text-nowrap',
      headClassName: '*:float-end',
    },
  },
  {
    id: 'salesAmount',
    accessorFn: ({ totalSale, totalClaim, totalSalesCreditNote }) => {
      const totalAmount = totalSale + totalClaim + totalSalesCreditNote;

      return totalAmount;
    },
    cell: CurrencyCell,
    enableSorting: false,
    header: 'Monto ventas',
    meta: {
      className: 'text-end text-nowrap',
      headClassName: '*:float-end text-end',
    },
  },
  {
    id: 'percentageCreditNote',
    cell({ row }) {
      const { totalSale, totalClaim, totalSalesCreditNote } = row.original;
      const totalAmount = totalSale + totalClaim + totalSalesCreditNote;
      const percentage = safeCalculatePercentage(
        totalSalesCreditNote,
        totalAmount,
      );

      return safeFormatToPercentage(percentage);
    },
    enableSorting: false,
    header: '% NC',
    meta: {
      className: 'text-end',
      headClassName: '*:float-end text-end',
    },
  },
  {
    id: 'totalSalesCreditNote',
    accessorKey: 'totalSalesCreditNote',
    cell: CurrencyCell,
    enableSorting: true,
    header: 'NC ventas',
    meta: {
      className: 'text-end text-nowrap',
      headClassName: '*:float-end',
    },
  },
  {
    id: 'totalClaim',
    accessorKey: 'totalClaim',
    cell: CurrencyCell,
    enableSorting: false,
    header: 'Reclamado',
    meta: {
      className: 'text-end text-nowrap',
      headClassName: '*:float-end text-end',
    },
  },
  {
    id: 'totalClaimAssignment',
    accessorKey: 'totalClaimAssignment',
    cell: CurrencyCell,
    enableSorting: true,
    header: 'Cedido reclamado',
    meta: {
      className: 'text-end text-nowrap',
      headClassName: '*:float-end',
    },
  },
  {
    id: 'totalCreditNoteAssignment',
    accessorKey: 'totalCreditNoteAssignment',
    cell: CurrencyCell,
    enableSorting: true,
    header: 'NC cedido',

    meta: {
      className: 'text-end text-nowrap',
      headClassName: '*:float-end',
    },
  },
  {
    id: 'totalAssignment',
    accessorKey: 'totalAssignment',
    cell: CurrencyCell,
    enableSorting: true,
    header: 'Cedido',
    meta: {
      className: 'text-end text-nowrap',
      headClassName: '*:float-end',
    },
  },
  {
    id: 'totalPurchase',
    accessorKey: 'totalPurchase',
    cell: CurrencyCell,
    enableSorting: true,
    header: 'Compras',
    meta: {
      className: 'text-end text-nowrap',
      headClassName: '*:float-end',
    },
  },
  {
    id: 'purchaseAmount',
    accessorFn({ totalPurchase, totalPurchasesCreditNote }) {
      const totalAmount = totalPurchase + totalPurchasesCreditNote;

      return totalAmount;
    },
    cell: CurrencyCell,
    enableSorting: false,
    header: 'Monto compras',
    meta: {
      className: 'text-end text-nowrap',
      headClassName: ' *:float-end text-end',
    },
  },
  {
    id: 'totalPurchasesCreditNote',
    accessorKey: 'totalPurchasesCreditNote',
    cell: CurrencyCell,
    enableSorting: true,
    header: 'NC compras',
    meta: {
      className: 'text-end text-nowrap',
      headClassName: '*:float-end',
    },
  },
];

export { columns };
